/*
 * A REPORT IN THE TRANSCRIPT — the card for `ai_admin_render_report`.
 *
 * Without it the call drew as a row named after the RPC, its document a page of escaped JSON. The
 * card shows what the person asked for: the report's title as it is written, then the PDF itself,
 * printed by the worker — the file they would download, so what they read here is what they send.
 * The download stays where every produced file's is, the chip under the turn; the card adds a link
 * that opens the same file full size.
 *
 * THE FILE IS READ, not the document re-rendered. A second renderer here would show an HTML page
 * that paginates differently from the PDF; the worker already printed the real thing and parked it
 * in the conversation, so the card fetches those bytes once (the chip's own read) and points an
 * iframe at them.
 *
 * NOT FOLDED: a report is made once or twice in a conversation, and the preview is the point.
 */
import { html, nothing } from 'lit';
import { until } from 'lit/directives/until.js';
import { registerWorkCard, partialString, type TurnTool } from '@vitesse/base-ui/ai';
import { t } from '@vitesse/base-ui/i18n';

export const REPORT_TOOL = 'ai_admin_render_report';

export interface ReportCardDeps {
  /** A file of this conversation, by sha — the read the transcript's download chips make. */
  read(sha256: string): Promise<Uint8Array | null>;
}

interface ReportFile {
  sha256: string;
  mediaType: string;
  filename: string;
}

/** The PDF the call produced, from the outcome's shared `artifacts` key. */
export function reportFile(tool: TurnTool): ReportFile | undefined {
  const files = (tool.data as { artifacts?: ReportFile[] } | undefined)?.artifacts ?? [];
  return files.find((f) => f.mediaType === 'application/pdf');
}

/** The pages it came to, as the worker read them off the printed file. */
export function reportPages(tool: TurnTool): number {
  return Number((tool.data as { pages?: number } | undefined)?.pages ?? 0);
}

/**
 * The document's title: parsed once the arguments are whole, read as far as it has arrived while
 * they stream, and from the start the archive kept of arguments too long to keep whole. Failing
 * all of those, the name the PDF was saved under.
 */
export function reportTitle(tool: TurnTool): string {
  const raw = tool.argumentsJson ?? '';
  let streamed = raw;
  try {
    const args = JSON.parse(raw) as { document_json?: string; truncated?: boolean; head?: string };
    if (typeof args.document_json === 'string') {
      const title = (JSON.parse(args.document_json) as { title?: unknown }).title;
      if (typeof title === 'string' && title.trim()) return title.trim();
    }
    if (args.truncated && typeof args.head === 'string') streamed = args.head;
  } catch {
    // Still arriving, or the document itself does not parse: read what is there.
  }
  const title = partialString(partialString(streamed, 'document_json'), 'title').trim();
  return title || reportFile(tool)?.filename || t('ai.reportCard');
}

/** Whether the model has finished writing the document, so what is left is the printing. */
const written = (tool: TurnTool): boolean => {
  try {
    JSON.parse(tool.argumentsJson ?? '');
    return true;
  } catch {
    return false;
  }
};

/*
 * ONE OBJECT URL PER FILE, kept for the page's life, as the transcript keeps decoded pictures: the
 * card repaints on every update of its turn, and a fresh fetch and URL each time would reload the
 * viewer under the reader. A read that failed is forgotten, so a later repaint tries again.
 */
const urls = new Map<string, Promise<string | null>>();

export function previewUrl(sha256: string, read: ReportCardDeps['read']): Promise<string | null> {
  let url = urls.get(sha256);
  if (!url) {
    url = read(sha256)
      .then((bytes) => (bytes ? URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' })) : null))
      .catch(() => null);
    urls.set(sha256, url);
    void url.then((u) => {
      if (!u) urls.delete(sha256);
    });
  }
  return url;
}

const note = (text: string) => (text ? html`<p class="wc-note">${text}</p>` : nothing);

/*
 * THE PREVIEW ITSELF, ONE PROMISE PER FILE, so every repaint hands `until` the same promise and Lit
 * leaves the iframe where it is. The transcript repaints whenever its own state moves — scrolling
 * past the title is one — and a fresh `.then()` each time was a value `until` had not seen: it drew
 * the placeholder, then a new iframe, and the PDF reloaded under the reader at every scroll. A file
 * that could not be read is dropped with its URL, so a later repaint asks again.
 */
const frames = new Map<string, Promise<unknown>>();

export function previewFrame(sha256: string, title: string, read: ReportCardDeps['read']): Promise<unknown> {
  let frame = frames.get(sha256);
  if (!frame) {
    frame = previewUrl(sha256, read).then((url) => {
      if (!url) {
        frames.delete(sha256);
        return note(t('ai.reportUnavailable'));
      }
      return html`<iframe class="report-frame" src=${`${url}#navpanes=0&view=FitH`} title=${title}></iframe>
        <a class="report-open" href=${url} target="_blank" rel="noopener">${t('ai.reportOpen')}</a>`;
    });
    frames.set(sha256, frame);
  }
  return frame;
}

const styles = html`<style>
  .report-frame {
    display: block;
    width: 100%;
    /* With the link under it, inside the 320px a card body keeps to (base-ui .wc-body). */
    height: 276px;
    border: 0;
    border-bottom: 1px solid var(--border-color);
    background: #fff;
  }
  .report-open {
    display: inline-block;
    margin: 8px 12px;
    font-size: 12px;
    color: var(--blue, var(--text-secondary));
    text-decoration: none;
  }
  .report-open:hover {
    text-decoration: underline;
  }
</style>`;

/** Claim `ai_admin_render_report`. Returns the undo. */
export function registerReportCard(deps: ReportCardDeps): () => void {
  return registerWorkCard(REPORT_TOOL, {
    title: reportTitle,
    status: (tool) => {
      if (tool.state === 'running') return written(tool) ? t('ai.reportPrinting') : t('ai.workWriting');
      if (tool.state === 'error') return t('ai.workFailed');
      const pages = reportPages(tool);
      return pages ? t('ai.reportPages', { count: pages }) : t('ai.workReady');
    },
    body: (tool) => {
      if (tool.state === 'running' || tool.state === 'unknown') return note(t('ai.reportArriving'));
      // A refusal is the finding — what was wrong with the document — so its sentence is the body.
      if (tool.state === 'error') return note(tool.message ?? '');
      const pdf = reportFile(tool);
      if (!pdf) return note(tool.message ?? '');
      return html`${styles}${until(previewFrame(pdf.sha256, reportTitle(tool), deps.read), note(t('ai.reportLoading')))}`;
    },
  });
}
